// Libraries
import * as yup from "yup";
import { DateTime } from "luxon";

// Utils
import { isAllZeroPhin, normalizeHealthCard } from "Utils/Normalization";

// Regex
import {
  nameCharactersRegex,
  alphaNumericRegex,
} from "Validation/validationRegex";

const isEmptyOrNonWhitespace = (value) => {
  if (typeof value !== "string") return true;
  return value.length === 0 || value.trim().length > 0;
};

const optionalNonWhitespaceString = (message) =>
  yup.string().test("no-whitespace-only", message, function () {
    return isEmptyOrNonWhitespace(this.originalValue);
  });

const hasValidHealthCardFormat = (value) => {
  if (!value) return true;
  const normalized = normalizeHealthCard(value);

  return (
    alphaNumericRegex.test(normalized) &&
    normalized.length >= 8 &&
    normalized.length <= 12
  );
};

export const mbsSearchValidationSchema = (t) => {
  const lastNameMessage = t("common.validation.nameCharacters");
  const healthCardMessage = t("mbsSearch.validation.healthCardMustBeValid");

  return yup
    .object({
      lastName: optionalNonWhitespaceString(lastNameMessage)
        .matches(nameCharactersRegex, lastNameMessage)
        .max(75, t("mbsSearch.validation.lastNameMax")),
      healthCardNumber: optionalNonWhitespaceString(healthCardMessage).test(
        "health-card-format",
        healthCardMessage,
        hasValidHealthCardFormat
      ),
      dateOfBirth: yup
        .mixed()
        .nullable()
        .test(
          "valid-date-of-birth",
          t("mbsSearch.validation.invalidDateOfBirth"),
          function (value) {
            if (!value) return true;
            if (!value?.isValid) return false;

            const formatted = value.toFormat("yyyy-MM-dd");
            if (!DateTime.fromFormat(formatted, "yyyy-MM-dd").isValid) {
              return false;
            }

            if (value.year <= 1900) {
              return this.createError({
                message: t("mbsSearch.validation.birthYearGreaterThan1900"),
              });
            }
            if (value > DateTime.now().endOf("day")) {
              return this.createError({
                message: t("mbsSearch.validation.dateCannotBeInFuture"),
              });
            }
            return true;
          }
        ),
    })
    .test(
      "at-least-one",
      t("mbsSearch.validation.atLeastOneField"),
      function (values) {
        const hasLastName = Boolean(values?.lastName);
        const hasHealthCardNumber = Boolean(values?.healthCardNumber);
        const hasDateOfBirth = Boolean(values?.dateOfBirth);

        if (hasLastName || hasHealthCardNumber || hasDateOfBirth) return true;

        return this.createError({
          path: "atLeastOneField",
          message: t("mbsSearch.validation.atLeastOneField"),
        });
      }
    )
    .test(
      "all-0s-requires-dob",
      t("mbsSearch.validation.healthCardRequiresDOB"),
      function (values) {
        const hasAllZeroHealthCardNumber = isAllZeroPhin(
          values?.healthCardNumber
        );

        if (!hasAllZeroHealthCardNumber) return true;

        const hasDateOfBirth = Boolean(values?.dateOfBirth);

        if (hasAllZeroHealthCardNumber && !hasDateOfBirth) {
          return this.createError({
            path: "dateOfBirth",
            message: t("mbsSearch.validation.healthCardRequiresDOB"),
          });
        }

        return true;
      }
    );
};
